var React = require('react');
const { useEffect, useState, useMemo, useRef } = require('react');
const { useTranslation, withTranslation } = require('react-i18next');
const NotificationsList = require('./NotificationsList');
const { connect, useSelector, useDispatch } = require('react-redux');
var userActions = require('userActions');
var distributionActions = require('distributionActions');
var distributionManager = require('@managers/distributionManager');
var { upper } = require('helpers');
const ComplexSelect = require('ComplexSelect');
const { resolveWatchedSelection } = require('./watchedEntitiesHelpers');
const {
  selectActiveFieldNotifications,
} = require('@utils/activeFieldNotifications');
const Spinner = require('../Spinner.jsx');

const CLIENT_PAGE_SIZE = 30;

const NotificationsCenterContainer = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectAllObject = {
    key: 'all',
    title: upper(t('all')),
    isSelected: true,
  };

  const [
    shouldDisplayWelcomeNotification,
    setShouldDisplayWelcomeNotification,
  ] = useState(false);
  const [distributionsOptions, setDistributionsOptions] = useState([]);
  const [selectedDistribution, setSelectedDistribution] = useState({});
  const [selectedFarmingUnit, setSelectedFarmingUnit] = useState({});
  const [selectedField, setSelectedField] = useState({});
  const [farmingUnitsOptions, setFarmingUnitsOptions] = useState([]);
  const [fieldsOptions, setFieldsOptions] = useState([]);
  const [enableSelection, setEnableSelection] = useState({
    distribution: false,
    farmingUnit: false,
    field: false,
  });
  const [visibleCount, setVisibleCount] = useState(CLIENT_PAGE_SIZE);
  // True while a picked distribution's full tree is loading — field
  // dropdown stays empty until fields hydrate; FU names come from skeleton.
  const [distributionTreeLoading, setDistributionTreeLoading] = useState(false);
  // Init filter options once for the lifetime of this mounted header widget.
  // Do NOT clear on modal close — otherwise navigating to a field (which merges
  // a full tree into all_distributions) or a refetch on reopen re-runs init and
  // resets the user's distribution filter back to "All".
  const didInitFilterRef = useRef(false);
  const selectedDistKeyRef = useRef(null);
  const selectedFuKeyRef = useRef(null);
  const selectedFieldKeyRef = useRef('all');
  // Which modal-open the watched-entities default has already been applied for.
  const appliedOpenCountRef = useRef(null);

  const all_distributions = useSelector(
    (state) => state.distribution_data.distributions,
  );
  const watchedDistributionId = useSelector(
    (state) => state.distribution_data.selected_entities.distribution?.id,
  );
  const watchedFarmingUnitId = useSelector(
    (state) => state.distribution_data.selected_entities.farming_unit?.id,
  );
  const notificationsFeed = useSelector(
    (state) => state.authentication_data.notifications_feed,
  );
  const isSyncing = notificationsFeed?.loadingSync || false;

  const feedItems = useSelector(selectActiveFieldNotifications);

  const filteredNotificationsData = useMemo(() => {
    if (!selectedDistribution?.key || selectedDistribution.key === 'all') {
      return feedItems;
    }
    return feedItems.filter((notification) => {
      if (notification.distribution_id !== selectedDistribution.key)
        return false;
      const farmingUnitMatches =
        selectedFarmingUnit?.key === 'all' ||
        notification.farming_unit_id === selectedFarmingUnit?.key;
      const fieldMatches =
        selectedField?.key === 'all' ||
        notification.field_id === selectedField?.key;
      return farmingUnitMatches && fieldMatches;
    });
  }, [feedItems, selectedDistribution, selectedFarmingUnit, selectedField]);

  useEffect(() => {
    setVisibleCount(CLIENT_PAGE_SIZE);
  }, [feedItems, selectedDistribution, selectedFarmingUnit, selectedField]);

  useEffect(() => {
    if (all_distributions === null) {
      return;
    }

    const [distributionsArr, farmingUnitsArr, fieldsArr] = populateLists();

    if (!didInitFilterRef.current) {
      const distMap = all_distributions?.distributions || {};
      const realDistIds = Object.keys(distMap);
      const multiDist = realDistIds.length > 1;

      didInitFilterRef.current = true;

      // Single distribution from skeleton — select it (no "All"). If it has a
      // single farming unit, select that too so FU/field filters can enable.
      if (!multiDist && realDistIds[0]) {
        const tree = distMap[realDistIds[0]];
        const distOption = {
          farming_units: tree.farming_units,
          key: tree.id,
          title: tree.name,
          isSelected: true,
        };
        let farmingUnitsForDist = [];
        [[], farmingUnitsForDist] = populateLists(tree);
        const realFus = farmingUnitsForDist.filter((fu) => fu.key !== 'all');
        const onlyFu = realFus.length === 1 ? realFus[0] : null;
        const initialFu =
          onlyFu ||
          farmingUnitsForDist.find((fu) => fu.key === 'all') ||
          selectAllObject;

        let fieldsForFu = [];
        if (onlyFu) {
          [[], [], fieldsForFu] = populateLists(undefined, onlyFu);
        }

        selectedDistKeyRef.current = distOption.key;
        selectedFuKeyRef.current = initialFu.key || 'all';
        selectedFieldKeyRef.current = 'all';

        setDistributionsOptions(
          distributionsArr.map((distribution) => ({
            ...distribution,
            isSelected: distribution.key === distOption.key,
          })),
        );
        setFarmingUnitsOptions(
          farmingUnitsForDist.map((farmingUnit) => ({
            ...farmingUnit,
            isSelected: farmingUnit.key === initialFu.key,
          })),
        );
        setFieldsOptions(
          fieldsForFu.map((field) => ({
            ...field,
            isSelected: field.key === 'all',
          })),
        );
        setSelectedDistribution(distOption);
        setSelectedFarmingUnit(initialFu);
        setSelectedField(selectAllObject);
        setEnableSelection({
          distribution: false,
          farmingUnit: realFus.length > 1,
          field: fieldsForFu.length > 1,
        });

        if (!distributionManager.isFullDistributionTree(tree)) {
          // Hydrate fields for the only dist; preserve effect reapplies options.
          setDistributionTreeLoading(
            !!(initialFu.key && initialFu.key !== 'all'),
          );
          dispatch(distributionActions.load_distribution_by_id(tree.id)).catch(
            (err) => {
              console.error(
                '[NotificationsCenter] distribution tree load error:',
                err,
              );
              setDistributionTreeLoading(false);
            },
          );
        }
        return;
      }

      // Multiple distributions — default everything to All.
      setDistributionsOptions(distributionsArr);
      setFarmingUnitsOptions(farmingUnitsArr);
      setFieldsOptions(fieldsArr);
      setSelectedDistribution(distributionsArr[0] || selectAllObject);
      setSelectedFarmingUnit(farmingUnitsArr[0] || selectAllObject);
      setSelectedField(selectAllObject);
      selectedDistKeyRef.current = distributionsArr[0]?.key || 'all';
      selectedFuKeyRef.current = farmingUnitsArr[0]?.key || 'all';
      selectedFieldKeyRef.current = 'all';
      setEnableSelection({
        distribution: true,
        farmingUnit: false,
        field: false,
      });
      return;
    }

    // Later tree merges / refetches: refresh dropdown membership + labels, but
    // keep the user's current dist / FU / field selection keys.
    const distKey = selectedDistKeyRef.current || 'all';
    const fuKey = selectedFuKeyRef.current || 'all';
    const fieldKey = selectedFieldKeyRef.current || 'all';

    setDistributionsOptions(
      distributionsArr.map((distribution) => ({
        ...distribution,
        isSelected: distribution.key === distKey,
      })),
    );

    const selectedDistOption =
      distributionsArr.find((distribution) => distribution.key === distKey) ||
      distributionsArr[0] ||
      selectAllObject;

    if (!distKey || distKey === 'all') {
      setSelectedDistribution(selectedDistOption);
      setSelectedFarmingUnit(
        farmingUnitsArr.find((farmingUnit) => farmingUnit.key === fuKey) ||
          farmingUnitsArr[0] ||
          selectAllObject,
      );
      setSelectedField(
        fieldsArr.find((field) => field.key === fieldKey) || selectAllObject,
      );
      setFarmingUnitsOptions(
        farmingUnitsArr.map((farmingUnit) => ({
          ...farmingUnit,
          isSelected: farmingUnit.key === fuKey,
        })),
      );
      setFieldsOptions(
        fieldsArr.map((field) => ({
          ...field,
          isSelected: field.key === fieldKey,
        })),
      );
      setEnableSelection({
        distribution:
          Object.keys(all_distributions?.distributions || {}).length > 1,
        farmingUnit: false,
        field: false,
      });
      return;
    }

    const tree =
      all_distributions?.distributions?.[distKey] || selectedDistOption;
    let nextFarmingUnitsArr = [];
    let nextFieldsArr = [];
    [[], nextFarmingUnitsArr, nextFieldsArr] = populateLists(tree);

    const selectedFuOption =
      (!fuKey || fuKey === 'all'
        ? nextFarmingUnitsArr.find((farmingUnit) => farmingUnit.key === 'all')
        : nextFarmingUnitsArr.find(
            (farmingUnit) => farmingUnit.key === fuKey,
          )) ||
      nextFarmingUnitsArr[0] ||
      selectAllObject;

    // Keep refs aligned when the preferred key disappeared after a refetch.
    if (selectedFuOption?.key && selectedFuOption.key !== fuKey) {
      selectedFuKeyRef.current = selectedFuOption.key;
    }

    if (selectedFuOption?.key && selectedFuOption.key !== 'all') {
      [[], [], nextFieldsArr] = populateLists(undefined, selectedFuOption);
    } else {
      nextFieldsArr = [];
    }

    const preservedField =
      nextFieldsArr.find((field) => field.key === fieldKey) || selectAllObject;
    if (preservedField?.key && preservedField.key !== fieldKey) {
      selectedFieldKeyRef.current = preservedField.key;
    }

    if (distributionManager.isFullDistributionTree(tree)) {
      setDistributionTreeLoading(false);
    }

    setSelectedDistribution({ ...selectedDistOption, isSelected: true });
    setSelectedFarmingUnit({ ...selectedFuOption, isSelected: true });
    setSelectedField(preservedField);
    setFarmingUnitsOptions(
      nextFarmingUnitsArr.map((farmingUnit) => ({
        ...farmingUnit,
        isSelected: farmingUnit.key === selectedFuOption.key,
      })),
    );
    setFieldsOptions(
      nextFieldsArr.map((field) => ({
        ...field,
        isSelected: field.key === preservedField.key,
      })),
    );
    setEnableSelection({
      distribution:
        Object.keys(all_distributions?.distributions || {}).length > 1,
      farmingUnit: nextFarmingUnitsArr.length > 1,
      field:
        nextFieldsArr.length > 1 &&
        selectedFuOption.key &&
        selectedFuOption.key !== 'all',
    });
  }, [all_distributions]);

  // Focus the filters on the entities the user is currently watching instead of
  // defaulting to "All". Applied on mount and again on every reopen, so the
  // panel follows the header's distribution / farming unit dropdowns — those
  // stay reachable while this widget remains mounted behind the modal.
  //
  // Gated on the open count rather than on the watched ids so that a filter the
  // user picks by hand is never reset underneath them while the panel is open,
  // and so later tree merges / refetches don't re-trigger it either.
  useEffect(() => {
    if (all_distributions === null) {
      return;
    }
    if (appliedOpenCountRef.current === props.openCount) {
      return;
    }
    appliedOpenCountRef.current = props.openCount;

    const [distributionsArr] = populateLists();
    const watchedSelection = resolveWatchedSelection({
      distributionsOptions: distributionsArr,
      getFarmingUnitsOptions: (distribution) => {
        let farmingUnitsArr = [];
        [[], farmingUnitsArr] = populateLists(distribution);
        return farmingUnitsArr;
      },
      watchedDistributionId,
      watchedFarmingUnitId,
    });
    if (!watchedSelection) {
      return;
    }

    // Reuses the change handlers so skeleton trees, the field spinner and the
    // post-load farming unit preservation all behave exactly as they do when
    // the user picks these entities by hand.
    onChangeDistribution(watchedSelection.distribution);
    if (watchedSelection.farmingUnit) {
      // Field is intentionally left on "All" — only the distribution and the
      // farming unit follow the current selection.
      onChangeFarmingUnit(watchedSelection.farmingUnit);
    }
  }, [all_distributions, props.openCount]);

  useEffect(() => {
    if (
      Object.keys(props?.userData)?.length !== 0 &&
      props.userData.hasOwnProperty('meta_data') &&
      props.userData?.meta_data?.notifications?.welcome_notification
    ) {
      setShouldDisplayWelcomeNotification(true);
    } else {
      setShouldDisplayWelcomeNotification(false);
    }
  }, [props.userData]);

  const hasMultipleDistributions = () =>
    Object.keys(all_distributions?.distributions || {}).length > 1;

  const onChangeDistribution = (newSelectedDistribution) => {
    const distKey = newSelectedDistribution.key;
    selectedDistKeyRef.current = distKey;

    // Filter notifications by distribution_id immediately; tree load is async.
    setSelectedDistribution(newSelectedDistribution);
    setDistributionsOptions((prevState) =>
      prevState.map((distribution) => ({
        ...distribution,
        isSelected: distribution.key === distKey,
      })),
    );

    if (distKey === 'all') {
      setDistributionTreeLoading(false);
      selectedFuKeyRef.current = 'all';
      selectedFieldKeyRef.current = 'all';
      const [[], farmingUnitsArr, fieldsArr] = populateLists();
      setSelectedFarmingUnit(selectAllObject);
      setSelectedField(selectAllObject);
      setFarmingUnitsOptions(farmingUnitsArr);
      setFieldsOptions(fieldsArr);
      setEnableSelection({
        distribution: hasMultipleDistributions(),
        farmingUnit: false,
        field: false,
      });
      return;
    }

    const distId = distKey;
    const distributionForLists =
      all_distributions?.distributions?.[distId] || newSelectedDistribution;

    const applyFuFieldFromTree = (tree, { preserveFu = false } = {}) => {
      let farmingUnitsArr = [];
      let fieldsArr = [];
      let finalSelectedFieldState = {};
      let finalSelectedFarmingUnitState = {};
      let enableEntitiesSelection = {};

      [[], farmingUnitsArr, fieldsArr] = populateLists(tree);

      const preferredFu =
        preserveFu &&
        selectedFuKeyRef.current &&
        selectedFuKeyRef.current !== 'all'
          ? farmingUnitsArr.find((fu) => fu.key === selectedFuKeyRef.current)
          : null;

      if (preferredFu) {
        [[], [], fieldsArr] = populateLists(undefined, preferredFu);
        finalSelectedFarmingUnitState = preferredFu;
        finalSelectedFieldState = selectAllObject;
        enableEntitiesSelection = {
          farmingUnits: farmingUnitsArr.length > 1,
          fields: fieldsArr.length > 1,
        };
      } else if (farmingUnitsArr.length > 1) {
        finalSelectedFarmingUnitState = selectAllObject;
        finalSelectedFieldState = selectAllObject;
        enableEntitiesSelection = {
          farmingUnits: true,
          fields: false,
        };
        selectedFuKeyRef.current = 'all';
      } else {
        finalSelectedFarmingUnitState = farmingUnitsArr[0] || selectAllObject;
        selectedFuKeyRef.current = finalSelectedFarmingUnitState.key || null;
        finalSelectedFieldState = selectAllObject;
        enableEntitiesSelection = {
          farmingUnits: false,
          fields: fieldsArr.length > 1,
        };
      }

      setDistributionTreeLoading(false);
      selectedFieldKeyRef.current = finalSelectedFieldState?.key || 'all';
      setSelectedFarmingUnit(finalSelectedFarmingUnitState);
      setSelectedField(finalSelectedFieldState);
      setFarmingUnitsOptions(farmingUnitsArr);
      setFieldsOptions(fieldsArr);
      setEnableSelection({
        distribution: hasMultipleDistributions(),
        farmingUnit: enableEntitiesSelection.farmingUnits,
        field: enableEntitiesSelection.fields,
      });
    };

    if (distributionManager.isFullDistributionTree(distributionForLists)) {
      applyFuFieldFromTree(distributionForLists);
      return;
    }

    // Skeleton already has FU id/name — populate that dropdown now. Only fields
    // need the full tree, so show a spinner on Field while it loads.
    setDistributionTreeLoading(true);
    selectedFuKeyRef.current = 'all';
    let skeletonFarmingUnitsArr = [];
    [[], skeletonFarmingUnitsArr] = populateLists(distributionForLists);

    if (skeletonFarmingUnitsArr.length > 1) {
      setSelectedFarmingUnit(selectAllObject);
      setFarmingUnitsOptions(skeletonFarmingUnitsArr);
      setEnableSelection({
        distribution: hasMultipleDistributions(),
        farmingUnit: true,
        field: false,
      });
    } else {
      const onlyFu = skeletonFarmingUnitsArr[0] || selectAllObject;
      selectedFuKeyRef.current = onlyFu.key || null;
      setSelectedFarmingUnit(onlyFu);
      setFarmingUnitsOptions(skeletonFarmingUnitsArr);
      setEnableSelection({
        distribution: hasMultipleDistributions(),
        farmingUnit: false,
        field: false,
      });
    }
    selectedFieldKeyRef.current = 'all';
    setSelectedField(selectAllObject);
    setFieldsOptions([]);

    dispatch(distributionActions.load_distribution_by_id(distId))
      .then((fullTree) => {
        if (selectedDistKeyRef.current !== distId || !fullTree) {
          return;
        }
        applyFuFieldFromTree(fullTree, { preserveFu: true });
      })
      .catch((err) => {
        console.error(
          '[NotificationsCenter] distribution tree load error:',
          err,
        );
        if (selectedDistKeyRef.current === distId) {
          setDistributionTreeLoading(false);
        }
      });
  };

  const onChangeFarmingUnit = (newSelectedFarmingUnit) => {
    let finalSelectedFieldState = {};
    let finalSelectedFarmingUnitState = {};
    let enableEntitiesSelection = {};
    let fieldsArr = [];

    selectedFuKeyRef.current = newSelectedFarmingUnit.key;
    selectedFieldKeyRef.current = 'all';

    if (newSelectedFarmingUnit.key !== 'all') {
      [[], [], fieldsArr] = populateLists(undefined, newSelectedFarmingUnit);

      finalSelectedFieldState = selectAllObject;
      finalSelectedFarmingUnitState = newSelectedFarmingUnit;
      enableEntitiesSelection = { field: fieldsArr.length > 1 };
    } else {
      finalSelectedFieldState = selectAllObject;
      finalSelectedFarmingUnitState = selectAllObject;
      enableEntitiesSelection = { field: false };
    }
    setSelectedFarmingUnit(newSelectedFarmingUnit);
    setSelectedField(finalSelectedFieldState);
    setFieldsOptions(fieldsArr);
    setEnableSelection({
      ...enableSelection,
      field: enableEntitiesSelection.field,
    });
    setFarmingUnitsOptions((prevState) => {
      return prevState.map((farmingUnit) => {
        farmingUnit.isSelected = farmingUnit.key === newSelectedFarmingUnit.key;
        return farmingUnit;
      });
    });
  };

  const onChangeField = (newSelectedField) => {
    selectedFieldKeyRef.current = newSelectedField.key;
    setSelectedField(newSelectedField);
    setFieldsOptions((prevState) => {
      return prevState.map((field) => {
        field.isSelected = field.key === newSelectedField.key;
        return field;
      });
    });
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + CLIENT_PAGE_SIZE);
  };

  const updateUserWelcomeNotification = () => {
    setShouldDisplayWelcomeNotification(false);
    let copyOfUserData = {};
    Object.assign(copyOfUserData, props.userData);

    copyOfUserData['meta_data'] = {
      notifications: {
        welcome_notification: false,
      },
    };
    props.setUserMetaData({
      meta_data_notifications_welcome:
        copyOfUserData['meta_data']['notifications']['welcome_notification'],
    });
    mixpanel.track('Notification Welcome Message Dismissed');
  };

  const populateLists = (distribution = undefined, farmingUnit = undefined) => {
    let distributionsArr = [];
    let farmingUnitsArr = [];
    let returnedDistributionsList = [];
    let returnedFarmingUnitsList = [];
    let returnedFieldsList = [];

    if (distribution === undefined && farmingUnit === undefined) {
      distributionsArr = Object.values(all_distributions?.distributions)
        ?.map((distribution) => {
          return {
            farming_units: distribution.farming_units,
            key: distribution.id,
            title: distribution.name,
            isSelected: false,
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title));
      farmingUnitsArr = distributionsArr
        .flatMap(
          (distribution) => Object.values(distribution?.farming_units) || [],
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    } else if (distribution !== undefined && farmingUnit === undefined) {
      farmingUnitsArr = Object.values(distribution?.farming_units).sort(
        (a, b) => a.name.localeCompare(b.name),
      );
    } else if (distribution === undefined && farmingUnit !== undefined) {
      farmingUnitsArr = [farmingUnit];
    }
    returnedDistributionsList = distributionsArr;
    // Skeleton FUs have no `fields` yet — keep them so the distribution filter
    // can enable from the spine. Once a tree is hydrated, keep only FUs that
    // still have at least one active field.
    returnedFarmingUnitsList = farmingUnitsArr
      .filter((farmingUnit) => {
        if (farmingUnit?.fields === undefined) {
          return true;
        }
        return Object.values(farmingUnit.fields).some(
          (field) => field?.settings?.field_state === 'active',
        );
      })
      .map((farmingUnit) => {
        return {
          fields: farmingUnit.fields,
          key: farmingUnit.id || farmingUnit.key,
          title: farmingUnit.name || farmingUnit.title,
          isSelected: false,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    returnedFieldsList = returnedFarmingUnitsList
      ?.flatMap((farmingUnit) =>
        Object.values(farmingUnit?.fields || {}).filter(
          (field) => field.settings.field_state === 'active',
        ),
      )
      .map((field) => {
        return {
          key: field.id || field.key,
          title: field.name || field.title,
          isSelected: false,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
    // Callers own enableSelection — populateLists must stay pure so tree merges
    // / feed refetches that rebuild options cannot lock or reset the filters.
    if (returnedDistributionsList?.length > 1) {
      returnedDistributionsList?.unshift({
        key: 'all',
        title: upper(t('all')),
        isSelected: true,
      });
    }
    if (returnedFarmingUnitsList?.length > 1) {
      returnedFarmingUnitsList?.unshift({
        key: 'all',
        title: upper(t('all')),
        isSelected: true,
      });
    }
    if (returnedFieldsList?.length >= 1) {
      returnedFieldsList?.unshift({
        key: 'all',
        title: upper(t('all')),
        isSelected: true,
      });
    }
    return [
      returnedDistributionsList,
      returnedFarmingUnitsList,
      returnedFieldsList,
    ];
  };

  const visibleNotifications = filteredNotificationsData.slice(0, visibleCount);
  const hasMore = visibleCount < filteredNotificationsData.length;

  return (
    <div>
      <div
        style={{
          borderBottom: '1px solid #DCE0E8',
          borderTop: '1px solid #DCE0E8',
          flexDirection: 'row',
          textAlign: 'center',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
        }}
      >
        {distributionsOptions?.length > 1 && (
          <ComplexSelect
            isDisable={!enableSelection.distribution}
            menuOptions={distributionsOptions}
            selectedItem={selectedDistribution}
            onItemClick={onChangeDistribution}
            label={t('distribution')}
          />
        )}
        {(farmingUnitsOptions?.length > 1 ||
          distributionsOptions?.length > 1) && (
          <ComplexSelect
            isDisable={!enableSelection.farmingUnit}
            menuOptions={farmingUnitsOptions}
            selectedItem={selectedFarmingUnit}
            onItemClick={onChangeFarmingUnit}
            label={t('farming_unit')}
          />
        )}
        <ComplexSelect
          isDisable={!enableSelection.field}
          menuOptions={fieldsOptions}
          selectedItem={selectedField}
          onItemClick={onChangeField}
          label={
            <span style={{ position: 'relative', display: 'inline-block' }}>
              {t('field')}
              {distributionTreeLoading &&
                selectedDistribution?.key &&
                selectedDistribution.key !== 'all' &&
                selectedFarmingUnit?.key &&
                selectedFarmingUnit.key !== 'all' && (
                  <span
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginLeft: '6px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Spinner size='small' />
                  </span>
                )}
            </span>
          }
        />
      </div>
      <NotificationsList
        onCloseClicked={props.onCloseClicked}
        shouldDisplayWelcomeNotification={shouldDisplayWelcomeNotification}
        updateUserWelcomeNotification={updateUserWelcomeNotification}
        notificationsData={visibleNotifications}
        isSyncing={isSyncing}
        hasAnyFeedItems={feedItems.length > 0}
      />
      {hasMore && (
        <div
          style={{
            textAlign: 'center',
            padding: '16px',
            cursor: 'pointer',
            color: '#4563C7',
            fontSize: '14px',
          }}
          onClick={handleLoadMore}
        >
          {t('load_more')}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = function (state) {
  return {
    userData: state.authentication_data.user_data,
  };
};

const mapDispatchToProps = {
  setUserMetaData: userActions.set_user_meta_data,
};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(NotificationsCenterContainer));
